"use client";
// AppDownloadSheet — sayfanın altından yükselen "uygulamayı indir" sheet'i.
//
// GÖRÜNME KOŞULLARI (hepsi sağlanmalı):
//   • Cihaz iOS veya Android. Masaüstünde hiç basılmaz — mağaza CTA'sının orada
//     bir karşılığı yok, tıklayınca web sayfası açılıyor.
//   • Uygulamanın kendi webview'ı DEĞİL ("ReactNativeWebView"). Kullanıcı zaten
//     uygulamanın içindeyken uygulamayı indirmesini istemek anlamsız.
//   • isPurePage kapalı — mobil uygulama sayfa modunda kabuk öğeleri basılmıyor.
//   • Daha önce X'e basılmamış (localStorage kaydı, TTL yok → bir daha görünmez).
//     Kayıt tarayıcı profiline bağlı olduğu için gizli sekmede sheet yeniden
//     görünür — kasıtlı.
//
// ÇEREZ BANNER'INDAN BAĞIMSIZ. Eskiden burada bir de "çerez banner'ı ekranda
// değilse" koşulu vardı ve consent KAYDINA (cookie_consent) bakıyordu. Banner'ın
// gerçek görünürlüğü ayrı bir anahtara (cookie_banner_seen) taşınınca ikisi
// ayrıştı: banner mount'ta consent'i kendiliğinden yazdığı için kayıt hep
// doluydu ama banner ekranda kalmaya devam ediyordu. Sonuç, taze profilde —yani
// HER gizli sekmede— sheet'in hiç açılmaması, sonraki ziyaretlerde açılmasıydı;
// "bazen çıkıyor bazen çıkmıyor" şikâyetinin sebebi buydu. Koşul tamamen
// kaldırıldı: sheet artık çerez tarafındaki hiçbir duruma bakmıyor.
//
// Mağaza linki ve başlık settings'ten geliyor — sabit yazılmadı:
//   settings.footer_apps = "{ title, items: [{ key, title, icon, url }] }" (JSON string)
//   key: "app_store" (iOS) | "play_store" (Android)
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Download, X } from "lucide-react";
import useSettings from "@/app/lib/features/settings/settings.hook";
import useMobile from "@/app/lib/features/mobile/mobile.hook";
import { storage, STORAGE_KEYS } from "@/app/lib/storage/storage";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const OPEN_DELAY_MS = 2000;
const CLOSE_DURATION_MS = 300;
const FALLBACK_LOGO = "/images/tozlu.png";

type Platform = "ios" | "android";

/** Platforma göre değişen tek şeyler: hangi mağaza kaydı okunacak ve alt satırdaki isim. */
const PLATFORM_COPY: Record<Platform, { storeKey: string; storeName: string }> = {
  ios: { storeKey: "app_store", storeName: "App Store" },
  android: { storeKey: "play_store", storeName: "Google Play" },
};

const detectPlatform = (): Platform | null => {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  // iPadOS 13+ kendini masaüstü Safari olarak tanıtıyor (UA'da "Macintosh").
  // Ayırt eden tek işaret çoklu dokunma desteği.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  return null;
};

interface FooterApp {
  key: string;
  title: string;
  icon: string;
  url: string;
}

const parseFooterApps = (
  raw?: string,
): { title?: string; items?: FooterApp[] } => {
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const AppDownloadSheet = () => {
  const { settings } = useSettings();
  const { isPurePage } = useMobile();

  // Platform ve localStorage yalnızca client'ta okunabilir; render sırasında
  // okunursa SSR HTML'iyle uyuşmazlık olur. Bu yüzden effect'te belirleniyor,
  // ilk render daima null döndürüyor.
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Tek kapı: uygulamanın webview'ı değilse ve daha önce kapatılmadıysa göster.
  // Her sayfa yüklemesinde yeniden değerlendirilir; kapatılana kadar görünür.
  useEffect(() => {
    if ("ReactNativeWebView" in window) return;
    if (storage.get<boolean>(STORAGE_KEYS.APP_PROMO_DISMISSED)) return;
    setPlatform(detectPlatform());
  }, []);

  // Açılış timer'ı. Mount ile "açık" durumun AYRI commit'lere düşmesi şart:
  // ikisi aynı commit'te olsaydı tarayıcı kapalı kareyi hiç boyamaz ve sheet
  // animasyonsuz belirirdi. 2 saniyelik gecikme bunu zaten garantiliyor.
  useEffect(() => {
    if (!platform || isPurePage) return;
    const timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [platform, isPurePage]);

  if (!platform || isPurePage) return null;

  const { storeKey, storeName } = PLATFORM_COPY[platform];
  const apps = parseFooterApps(settings?.footer_apps);
  const app = apps.items?.find((item) => item.key === storeKey);

  // Bu platformun mağaza linki settings'te yoksa sheet hiç basılmaz —
  // tıklanamayan bir CTA göstermektense hiç göstermemek doğru.
  if (!app?.url) return null;

  const handleDismiss = () => {
    storage.set(STORAGE_KEYS.APP_PROMO_DISMISSED, true);
    setDismissed(true);
  };

  const visible = open && !dismissed;

  return (
    // Kapanışta DOM'dan kaldırılmıyor, ekran dışına kaydırılıyor: kaldırılsaydı
    // kapanış animasyonu hiç görünmezdi. pointer-events-none kapalıyken altındaki
    // sayfanın tıklanabilir kalmasını sağlıyor.
    <div
      className={`fixed inset-x-0 bottom-0 z-998 transition-transform ease-out ${
        visible ? "translate-y-0" : "translate-y-full pointer-events-none"
      }`}
      style={{ transitionDuration: `${CLOSE_DURATION_MS}ms` }}
      // inert: kapalıyken içerideki X butonu klavyeyle odaklanabilir ve ekran
      // okuyucuda kalırdı. aria-hidden tek başına odaklanmayı engellemiyor.
      inert={!visible}
    >
      <div className="relative mx-auto w-full max-w-md rounded-t-2xl border-t border-gray-100 bg-white px-6 pt-7 pb-8 text-center shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Kapat"
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X size={16} />
        </button>

        {/* Boyut YALNIZCA `h-8` sınıfından geliyor; `width`/`height` prop'ları
            sadece oranı bildiriyor (logo 1563x469 ≈ 3.33:1).
            Buraya inline `style={{height:"auto"}}` EKLEMEYİN: width ve height'in
            ikisi birden `auto` olunca <img>'in width/height attribute'ları
            (sunum ipucu oldukları için) tamamen yok sayılıyor, geriye Tailwind
            preflight'ın `img{max-width:100%}` kuralı kalıyor ve logo kartın tam
            genişliğine yayılıyor. Prop'taki sayıyı değiştirmek hiçbir işe
            yaramıyordu, ölçüyü büyütüp küçültmek için h-* değerini oynatın. */}
        <Image
          src={settings?.site_logo || FALLBACK_LOGO}
          alt={settings?.site_name || "Tozlu"}
          width={107}
          height={32}
          className="mx-auto h-8 w-auto object-contain"
          unoptimized
        />

        <h2 className="mt-5 text-base font-bold text-gray-900">
          {apps.title || "Mobil Uygulamamızı İndirin"}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          Daha iyi bir deneyim için uygulamamızı indirin
        </p>

        <Link
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackGeneric(app.key, "click")}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
        >
          <Download size={18} />
          Uygulamayı İndir
        </Link>

        <p className="mt-4 text-xs text-gray-400">
          {storeName}&apos;den ücretsiz indirin
        </p>
      </div>
    </div>
  );
};

export default AppDownloadSheet;
